import { Tooltip as AntTooltip, TooltipProps as AntTooltipProps } from 'antd';
import { ReactNode } from 'react';

export type TooltipPlacement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight'
  | 'left'
  | 'leftTop'
  | 'leftBottom'
  | 'right'
  | 'rightTop'
  | 'rightBottom';

export interface TooltipProps
  extends Omit<AntTooltipProps, 'title' | 'overlay'> {
  /** Content to show in tooltip */
  content: ReactNode;
  /** Element that triggers the tooltip */
  children: ReactNode;
  /** Placement of tooltip relative to target */
  placement?: TooltipPlacement;
  /** Whether tooltip is disabled */
  disabled?: boolean;
  /** Delay before showing (ms) */
  showDelay?: number;
  /** Delay before hiding (ms) */
  hideDelay?: number;
}

/**
 * Tooltip component based on Libertify Design System
 *
 * Styled via antdTheme configuration for consistent theming.
 *
 * @example
 * ```tsx
 * import { Tooltip, Button } from '@libertify/ui';
 *
 * <Tooltip content="This is helpful information">
 *   <Button>Hover me</Button>
 * </Tooltip>
 *
 * // With placement
 * <Tooltip content="Info" placement="right">
 *   <span>Hover</span>
 * </Tooltip>
 * ```
 */
export const Tooltip = ({
  content,
  children,
  placement = 'top',
  disabled = false,
  showDelay = 0,
  hideDelay = 0,
  ...props
}: TooltipProps) => {
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <AntTooltip
      title={content}
      placement={placement}
      mouseEnterDelay={showDelay / 1000}
      mouseLeaveDelay={hideDelay / 1000}
      {...props}
    >
      {children}
    </AntTooltip>
  );
};

export default Tooltip;
